import React, { forwardRef, useRef, useState } from 'react'
import { Button, Col, FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'
import cloneDeep from 'lodash/cloneDeep'
import ArrowUpLineIcon from '@rsuite/icons/ArrowUpLine'
import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine'
import useTranslation from 'next-translate/useTranslation'

import Newsletter from '../../LandingPage/Newsletter'
import RfqSection from './RfqSection'
import useProductCatalogCategoryContent from '../../../services/strapi/useProductCatalogCategoryContent'
import { QueryParam } from '../../../utils/constants'
import useIsOnMobile from '../../../services/useIsOnMobile'
import { Product } from '../../../utils/types/Product'
import SingleProductReviewsSection from '../../SingleProduct/ReviewsSection/SingleProductReviewsSection'
import ChatPanel from '../../ChatPanel'

import styles from '../../../styles/CatalogBottomSection.module.less'

interface CatalogBottomSectionProps {
  product?: Product
}

const firstTwoColumnProps = {
  as: Col,
  xs: 24,
  md: 12,
}

const CatalogBottomSection = forwardRef<HTMLDivElement, CatalogBottomSectionProps>(
  (props, forwardedRef) => {
    const { product } = props
    const [shouldExpandDescription, setShouldExpandDescription] = useState(false)

    const categoryBottomDescriptionRef = useRef<HTMLDivElement>(null)
    const { query } = useRouter()
    const isOnMobile = useIsOnMobile()
    const { t } = useTranslation('products')

    const productId = product?.id ?? ''
    const queryObject = cloneDeep(query)
    const categoryCode = Array.isArray(queryObject[QueryParam.filters])
      ? queryObject[QueryParam.filters]?.reverse()
        .find((path: string) => path.startsWith('c_')) || ''
      : ''

    const { categoryPageBottom } = useProductCatalogCategoryContent(categoryCode)

    // If description text is not that large then don't implement collapse behaviour
    const shouldShowEllipsisText = categoryBottomDescriptionRef.current
      && categoryBottomDescriptionRef.current.innerText.length < 650
    const showExpandedDescription = isOnMobile
    && !shouldExpandDescription
    && !shouldShowEllipsisText

    const renderReadMoreButton = () => {
      if (!isOnMobile || shouldShowEllipsisText) {
        return null
      }

      return (
        <Button
          className="padding-zero margin-top-spacer"
          appearance="link"
          onClick={() => setShouldExpandDescription((prev) => !prev)}
        >
          {shouldExpandDescription
            ? (
              <>
                {t('Close')}
                <ArrowUpLineIcon className="margin-left-spacer-half" />
              </>
            )
            : (
              <>
                {t('marketing:Read more') }
                <ArrowDownLineIcon className="margin-left-spacer-half" />
              </>
            )}
        </Button>
      )
    }

    return (
      <FlexboxGrid
        as="section"
        className={styles['section-container']}
      >
        <FlexboxGrid.Item {...firstTwoColumnProps}>
          <RfqSection />
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          {...firstTwoColumnProps}
          className={styles['chat-panel-item']}
        >
          <ChatPanel
            title={t('Need help?')}
            customTextContent={t('Get help from our experts')}
            hideBorder={!isOnMobile}
            hideArrow={!isOnMobile}
          />
        </FlexboxGrid.Item>
        {product && (
          <>
            <div ref={forwardedRef} />
            <SingleProductReviewsSection
              product={product}
              key={productId}
            />
          </>
        )}
        {categoryPageBottom && (
        <>
          <div
            ref={categoryBottomDescriptionRef}
            className={`margin-top-spacer ${
              styles[
                showExpandedDescription
                  ? 'category-bottom-mobile'
                  : 'category-bottom-description'
              ]
            }`}
          >
            {categoryPageBottom}
          </div>
          {renderReadMoreButton()}
        </>
        )}
        <FlexboxGrid.Item
          as={Col}
          xs={24}
        >
          <Newsletter />
        </FlexboxGrid.Item>
      </FlexboxGrid>
    )
  },
)

export default CatalogBottomSection
